import React, { useEffect, useMemo, useState } from 'react'
import { useAtom } from 'jotai'
import { nanoid } from 'nanoid'
import * as skedUI from '@skedulo/sked-ui'
const { Loading } = skedUI
import { DEFAULT_VIEW_STATE_ID } from '../constants'
import {
  useViewStateApiAtom,
  useViewStateLastActiveAtom,
  useViewStateManagement,
  useViewStateScope,
} from '../hooks'
import { viewStateStore } from '../store'
import { ViewStateContext } from './view-state-context'
import type { Scope } from '../types'

/**
 * Scope provider used to group child components under the given scope.
 */
export const ViewStateScope = ({
  children,
  scope,
  viewType,
  scopeId,
}: {
  children: React.ReactNode
  scope: string
  viewType: string
  scopeId?: string
}) => {
  const randId = useMemo(() => nanoid(), [])
  const newScope: Scope = {
    scope: scope,
    type: viewType,
    id: scopeId ?? randId,
  }

  return (
    <ViewStateContext.Provider
      value={{
        scope: newScope,
      }}
    >
      <ViewStateSetup>{children}</ViewStateSetup>
    </ViewStateContext.Provider>
  )
}
/**
 * Internal component used to set up hooks for the scope.
 */
export const ViewStateSetup = ({ children }: { children: React.ReactNode }) => {
  const scope = useViewStateScope()

  const {
    activeViewStateId,
    activateViewState,
    preferredDefaultId,
    viewStatesList,
    findPreferredDefaultViewState,
  } = useViewStateManagement()

  const { apiViewStates } = useViewStateApiAtom(scope)
  const [lastActiveId] = useAtom(useViewStateLastActiveAtom(scope), {
    store: viewStateStore,
  })

  const [ready, setReady] = useState(false)

  // Sets a non-default view when either:
  // 1. A custom default view has been provided.
  // 2. A last active view was found in local storage.
  // Option 1 takes precedence.
  useEffect(() => {
    // We don't have view states from the API yet
    if (!apiViewStates) {
      return
    }

    // We don't have anything to assert the default
    if (!preferredDefaultId && !lastActiveId) {
      setReady(true)
      return
    }

    // We aren't on the empty/default view state
    if (activeViewStateId !== DEFAULT_VIEW_STATE_ID) {
      setReady(true)
      return
    }

    const def = findPreferredDefaultViewState(
      apiViewStates,
      preferredDefaultId,
      lastActiveId,
    )
    if (def.id !== activeViewStateId) {
      activateViewState(def)
    }
    setReady(true)
  }, [
    preferredDefaultId,
    lastActiveId,
    activeViewStateId,
    activateViewState,
    apiViewStates,
    findPreferredDefaultViewState,
    viewStatesList,
  ])

  // eslint-disable-next-line react/jsx-no-useless-fragment
  if (ready) return <>{children}</>
  return <Loading />
}
